import { lang } from '@umu/ukit-lang';
import { If } from 'common/src/components';
import React, { Fragment, useMemo } from 'react';
import { ChartType, DATA_TYPE } from '../../interface';
import { IntervalChart, RadarChart } from '../../../chartDataView';
import { ICapabilityProps } from '../../../chartDataView/interface';
import { normalizedCompareChartProps, sortRadarData, transformChartCompareData } from '../../dataFlow';

interface IProps extends ICapabilityProps {
    pageType?: number;
    isDetail?: boolean;
    emptyText?: string;
    isCompare?: boolean;
    chartType?: ChartType;
    labelRowCount?: number;
    detailRadarSplitCount?: number;
    radarColor?: string | [string, (xVal: string) => string];
    onTooltipTpl?: (xVal: string, info: any) => React.ReactNode;
}

const RADAR_MAX_COUNT = 8; // 雷达图最大展示个数 超出展示柱状图
const RADAR_MIN_COUNT = 2; // 雷达图最小展示个数 超出展示柱状图

const intervalSplitCountMap: { [key: number]: number } = {
    1: 8,
    2: 7,
    3: 6,
    4: 5,
};

function CapabilityAnalysis(props: IProps) {
    const {
        data = [],
        chartType,
        onTooltipTpl,
        isDetail = false,
        isCompare = false,
        detailRadarSplitCount,
        radarColor = '#1e6ee6',
        pageType = DATA_TYPE.USER,
        emptyText = lang('暂无数据'),
        preCls = 'capability-analysis',
        ...rest
    } = props;

    const normalizedChartData = useMemo(() => {
        return isCompare ? transformChartCompareData(pageType, data, isDetail) : data;
    }, [data, isCompare, pageType, isDetail]);

    const sortNormalizedChartData = useMemo(() => {
        return isCompare ? sortRadarData(normalizedChartData) : normalizedChartData;
    }, [isCompare, normalizedChartData]);

    const normalizedChartProps = useMemo(() => {
        if (isCompare) {
            return normalizedCompareChartProps(
                pageType,
                {
                    radarChartProps: {
                        padding: [44, 0, 16, 0],
                        appendPadding: [10, 5, 5, 5],
                    },
                    intervalChartProps: {
                        padding: [40, 24, normalizedChartData.length > 15 ? 80 : 44, isDetail ? 38 : 54],
                    },
                },
                isDetail,
            );
        }

        return {
            radarColor,
            intervalColor: '#1e6ee6',
            radarChartProps: {
                appendPadding: [10, 5, 5, 5],
                padding: isDetail ? [24, 24, 24, 24] : [20, 40, 20, 40],
            },
            intervalChartProps: {
                padding: isDetail ? [40, 48, data.length > 15 ? 60 : 44, 48] : [40, 80, data.length > 15 ? 60 : 44, 80],
            },
        };
    }, [pageType, isDetail, isCompare, radarColor, normalizedChartData]);

    const intervalSplitCount = useMemo(() => {
        if (data.length <= 4) {
            return intervalSplitCountMap[data.length];
        }
        if (data.length >= 10) {
            return 3;
        }

        return 5;
    }, [data]);

    const [isInterval, isEmpty] = useMemo(() => {
        const isEmpty = data.length === 0;
        if (chartType) {
            return [chartType === ChartType.INTERVAL, isEmpty];
        }
        return [data.length > RADAR_MAX_COUNT || data.length <= RADAR_MIN_COUNT, isEmpty];
    }, [data, chartType]);

    const customTooltip = (xVal: string, sourceData: any[]) => {
        const dataInfo = sourceData?.[0]?.data;
        if (!dataInfo || !onTooltipTpl) {
            return null;
        }
        return onTooltipTpl(xVal, { ...dataInfo, isCompare });
    };

    return (
        <div className={preCls}>
            <If condition={isEmpty}>
                <div className={`${preCls}-empty`}>
                    <div className={`${preCls}-empty-img`} />
                    <div className={`${preCls}-empty-text`}>{emptyText}</div>
                </div>
            </If>
            <If condition={!isEmpty}>
                <Fragment>
                    {isInterval ? (
                        <IntervalChart
                            {...rest}
                            showInteraction
                            data={normalizedChartData}
                            customTooltip={customTooltip}
                            preCls={`${preCls}-interval`}
                            initSlider={10 / normalizedChartData.length}
                            isShowSlider={normalizedChartData.length > 15}
                            {...normalizedChartProps}
                            splitCount={intervalSplitCount}
                            color={normalizedChartProps?.intervalColor}
                        />
                    ) : (
                        <RadarChart
                            {...rest}
                            labelOffset={24}
                            preCls={`${preCls}-radar`}
                            customTooltip={customTooltip}
                            data={sortNormalizedChartData}
                            splitCount={rest.splitCount || 6}
                            selectLabelConfig={{ textBaseline: 'middle' }}
                            {...normalizedChartProps}
                            color={normalizedChartProps?.radarColor}
                        />
                    )}
                </Fragment>
            </If>
        </div>
    );
}

export { RADAR_MIN_COUNT, RADAR_MAX_COUNT, CapabilityAnalysis };
